import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useAdjustInventory,
  useInventorySearch,
  useVariantInventory,
  type VariantSearchResult,
} from '@/hooks/useAdmin'

const FIELDS = ['available', 'incoming', 'on_hand'] as const
type FieldName = (typeof FIELDS)[number]

const FIELD_LABEL: Record<FieldName, string> = {
  available: 'Available',
  incoming: 'Incoming',
  on_hand: 'On hand',
}

function variantLabel(v: VariantSearchResult): string {
  const parts = [v.product_title || '(untitled product)']
  if (v.variant_title && v.variant_title !== 'Default Title') parts.push(v.variant_title)
  return parts.join(' · ')
}

export function ManualInventorySection() {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [selected, setSelected] = useState<VariantSearchResult | null>(null)
  // Drafts: the in-progress values the user is typing in each field. Keyed by
  // location_id so we don't lose edits when switching focused location.
  const [drafts, setDrafts] = useState<Record<number, Partial<Record<FieldName, string>>>>({})
  // Location whose save is awaiting confirmation. Saving overwrites live
  // Shopify stock, so the button opens a confirm dialog first.
  const [confirmLocId, setConfirmLocId] = useState<number | null>(null)

  // Debounce search input → query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 250)
    return () => clearTimeout(t)
  }, [searchInput])

  const searchResults = useInventorySearch(debouncedQ)
  const variantInventory = useVariantInventory(selected?.variant_id ?? null)
  // A failed live read cancels any pending confirm, so the write dialog can't
  // reopen on its own when a later read succeeds — the user re-clicks Save.
  useEffect(() => {
    if (variantInventory.isError) setConfirmLocId(null)
  }, [variantInventory.isError])
  const adjustMut = useAdjustInventory()

  // When a different variant is selected, drop in-progress drafts.
  useEffect(() => {
    setDrafts({})
  }, [selected?.variant_id])

  const locations = variantInventory.data?.locations ?? []
  const primaryLocId = locations[0]?.location_id ?? null

  function fieldValue(locId: number, field: FieldName, fallback: number): string {
    const v = drafts[locId]?.[field]
    return v !== undefined ? v : String(fallback)
  }

  function setField(locId: number, field: FieldName, value: string) {
    setDrafts((prev) => ({
      ...prev,
      [locId]: { ...(prev[locId] ?? {}), [field]: value },
    }))
  }

  const dirtyByLocation = useMemo(() => {
    const map: Record<number, Partial<Record<FieldName, number>>> = {}
    for (const loc of locations) {
      if (loc.location_id == null) continue
      const drafted = drafts[loc.location_id]
      if (!drafted) continue
      const dirty: Partial<Record<FieldName, number>> = {}
      for (const f of FIELDS) {
        const raw = drafted[f]
        if (raw === undefined) continue
        const parsed = Number(raw)
        if (Number.isNaN(parsed)) continue
        if (parsed !== loc[f]) dirty[f] = parsed
      }
      if (Object.keys(dirty).length > 0) map[loc.location_id] = dirty
    }
    return map
  }, [drafts, locations])

  async function saveLocation(locId: number) {
    const dirty = dirtyByLocation[locId]
    if (!dirty || !selected) return
    try {
      const res = await adjustMut.mutateAsync({
        variant_id: selected.variant_id,
        location_id: locId,
        available: dirty.available,
        incoming: dirty.incoming,
        on_hand: dirty.on_hand,
      })
      const failures = res.results.filter((r) => !r.ok)
      if (failures.length === 0) {
        toast.success(
          `Saved ${res.results.filter((r) => r.delta !== 0).length} change(s) to Shopify`
        )
        setDrafts((prev) => {
          const next = { ...prev }
          delete next[locId]
          return next
        })
      } else {
        toast.error(
          `${failures.length} field(s) failed: ${failures.map((f) => `${f.name} (${f.error})`).join(', ')}`
        )
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Adjust failed'
      toast.error(msg)
    }
  }

  async function confirmSave() {
    if (confirmLocId == null) return
    await saveLocation(confirmLocId)
    setConfirmLocId(null)
  }

  const confirmLoc =
    confirmLocId == null ? null : locations.find((l) => l.location_id === confirmLocId) ?? null
  const confirmDirty = confirmLocId == null ? undefined : dirtyByLocation[confirmLocId]
  const confirmFields = confirmLoc && confirmDirty
    ? FIELDS.filter((f) => confirmDirty[f] !== undefined).map((f) => ({
        field: f,
        from: confirmLoc[f],
        to: confirmDirty[f] as number,
      }))
    : []

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Manual Inventory Adjustment</h2>
      <p className="text-xs text-[var(--mute)]">
        Search for a variant, then edit Available / Incoming / On hand directly in Shopify.
        Backend re-reads the live Shopify value at save time and pushes the delta — type the
        target value, not a delta.
      </p>

      {/* Search */}
      <div className="space-y-2">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by SKU, barcode, or product title…"
          className="w-full px-2 py-1 border border-[var(--line-input)] bg-[var(--surface-card)] text-xs focus:outline-none focus:border-[var(--brand)]"
          data-testid="manual-inventory-search"
        />

        {debouncedQ.length >= 2 && (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] max-h-60 overflow-auto">
            {searchResults.isLoading ? (
              <div className="p-2 space-y-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (searchResults.data ?? []).length === 0 ? (
              <div className="p-3 text-xs text-[var(--mute)]">No matches.</div>
            ) : (
              <ul>
                {(searchResults.data ?? []).map((v) => {
                  const isSelected = selected?.variant_id === v.variant_id
                  return (
                    <li key={v.variant_id}>
                      <button
                        type="button"
                        onClick={() => setSelected(v)}
                        className={`w-full text-left px-2 py-1.5 text-xs border-b border-[var(--line-warm)] last:border-b-0 hover:bg-[var(--surface-page)] ${isSelected ? 'bg-[var(--surface-header)] font-semibold' : ''}`}
                      >
                        <div>{variantLabel(v)}</div>
                        <div className="text-[10px] text-[var(--mute)] mt-0.5">
                          {v.vendor || '—'} · SKU {v.sku || '—'} · barcode {v.barcode || '—'}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Editor */}
      {selected && (
        <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 space-y-3">
          <div className="text-xs">
            <div className="font-semibold">{variantLabel(selected)}</div>
            <div className="text-[10px] text-[var(--mute)] mt-0.5">
              variant_id {selected.variant_id} · SKU {selected.sku || '—'} · barcode {selected.barcode || '—'}
            </div>
          </div>

          {variantInventory.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : variantInventory.isError ? (
            <div className="text-xs px-2 py-1 bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]">Could not load live Shopify state.</div>
          ) : locations.length === 0 ? (
            <div className="text-xs text-[var(--mute)]">
              No inventory levels found in Shopify for this variant.
              {primaryLocId == null && ' (Shopify creds may be missing.)'}
            </div>
          ) : (
            <div className="space-y-3">
              {locations.map((loc) => {
                if (loc.location_id == null) return null
                const locId = loc.location_id
                const isDirty = !!dirtyByLocation[locId]
                return (
                  <div key={locId} className="space-y-1.5">
                    <div className="flex items-center gap-2 text-[11px] text-[var(--ink-2)]">
                      <span className="font-semibold">{loc.location_name || `Location ${locId}`}</span>
                      <span className="text-[var(--mute)]">·</span>
                      <span className="text-[var(--mute)]">id {locId}</span>
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                      {FIELDS.map((f) => (
                        <label key={f} className="text-[10px] flex flex-col">
                          <span className="text-[var(--mute)]">
                            {FIELD_LABEL[f]} <span className="text-[var(--mute)]">(was {loc[f]})</span>
                          </span>
                          <input
                            type="number"
                            value={fieldValue(locId, f, loc[f])}
                            onChange={(e) => setField(locId, f, e.target.value)}
                            className="px-1 py-0.5 border border-[var(--line-input)] bg-[var(--surface-card)] text-xs tabular-nums focus:outline-none focus:border-[var(--brand)]"
                          />
                        </label>
                      ))}
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        disabled={!isDirty || adjustMut.isPending}
                        onClick={() => setConfirmLocId(locId)}
                      >
                        {adjustMut.isPending ? 'Saving…' : 'Save to Shopify…'}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] text-[var(--mute)]"
              onClick={() => { setSelected(null); setDrafts({}) }}
            >
              Clear selection
            </Button>
          </div>
        </div>
      )}

      {/* Save confirm — a live overwrite of Shopify stock. Shows exactly which
          numbers change so the consequence is named before it happens. */}
      {/* Gated on the live read: if a refetch fails, the "From" column would be
          stale data, so the dialog closes until Shopify answers again. */}
      <Dialog
        open={confirmLocId != null && confirmFields.length > 0 && !variantInventory.isError}
        onOpenChange={(open) => { if (!open) setConfirmLocId(null) }}
      >
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="manual-inventory-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle>Write inventory to Shopify?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-xs">
                {selected && (
                  <p>
                    <span className="font-semibold">{variantLabel(selected)}</span>
                    {' · '}SKU {selected.sku || '—'}
                    <br />
                    at{' '}
                    <span className="font-semibold">
                      {confirmLoc?.location_name || `Location ${confirmLocId}`}
                    </span>
                  </p>
                )}
                <table className="w-full tabular-nums">
                  <thead>
                    <tr className="text-[var(--mute)]">
                      <th className="tbl-th text-left">Field</th>
                      <th className="tbl-th text-right">From</th>
                      <th className="tbl-th text-right">To</th>
                      <th className="tbl-th text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmFields.map(({ field, from, to }) => {
                      const change = to - from
                      return (
                        <tr key={field} data-testid={`confirm-row-${field}`}>
                          <td>{FIELD_LABEL[field]}</td>
                          <td className="text-right">{from}</td>
                          <td className="text-right">{to}</td>
                          <td
                            className={`text-right ${change > 0 ? 'text-[var(--status-approved-fg)]' : 'text-[var(--status-rejected-fg)]'}`}
                          >
                            {change > 0 ? `+${change}` : `−${Math.abs(change)}`}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <p className="font-semibold text-[var(--status-awaiting-fg)]">
                  This writes live to Shopify and cannot be undone from the app.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmLocId(null)}
              disabled={adjustMut.isPending}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={confirmSave} disabled={adjustMut.isPending}>
              {adjustMut.isPending
                ? 'Saving…'
                : `Write ${confirmFields.length} ${confirmFields.length === 1 ? 'change' : 'changes'} to Shopify`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
